// What a configured order writes on the boat's order file. Every catalogue item of the configurator (Sales ›
// Configuration Setup) is tied to the order file's rows (domain/orderFile.ts, one per Order Sheet column): an
// option row it ticks ("Radar Raymarine Quantum Series" ticks RADAR and RAYMARINE) or a text row it fills in
// ("2x Mercury 400R V10" fills NUMBER OF with 2 and MERCURY with 400R V10). An item with no tie of its own
// gets one suggested from its name and its category, so most of a catalogue works from the start, and a
// person sets the rest on the Setup page.
//
// Confirming an order (or saving a new revision of it) then writes the boat's Order Sheet row from those ties:
// the model's standard equipment, the standard items kept, the options chosen and the custom requests on the
// additional-request rows. The order file and the boat's BOM (domain/bom.ts: the standard sections plus the
// option sections the Order Sheet row calls for) follow from that row, as they do for an order file filled in
// by hand.
import { z } from 'zod';
import { unlistedOrderKey } from './bom.js';
import { ORDER_FILE_FIELDS, ORDER_FILE_TICK, orderFileLabel, orderFileRow } from './orderFile.js';
const FIELD_KEYS = new Set(ORDER_FILE_FIELDS.map((f) => f.key));
/** whether a key is an order file row that takes a value */
export const isOrderCellField = (key) => FIELD_KEYS.has(key);
/** whether the row is ticked rather than written in */
export const orderCellIsTick = (field) => orderFileRow(field)?.kind === 'option';
export const OrderCellSchema = z.object({
    field: z.string().refine(isOrderCellField, 'Pick a row of the order file'),
    value: z.string().max(120).default(''),
});
/** an item's ties as saved; null (or absent) means "use the suggestion" */
export const OrderCellsSchema = z.array(OrderCellSchema).max(12);
/** the additional-request rows (AR1 … AR10) the custom requests go on, in order */
export const ORDER_FILE_REQUEST_KEYS = ORDER_FILE_FIELDS.filter((f) => /^AR\d+$/.test(f.en)).map((f) => f.key);
/** an item's ties in words: "RADAR, RAYMARINE" or "NUMBER OF 2, MERCURY 400R V10" */
export function orderCellsText(cells) {
    return cells.map((c) => (orderCellIsTick(c.field) ? orderFileLabel(c.field) : `${orderFileLabel(c.field)} ${c.value}`.trim())).join(', ');
}
// ── suggesting an item's ties ──────────────────────────────────────────────────────────────────
const fold = (s) => s.normalize('NFD').replace(/\p{M}/gu, '').toUpperCase().replace(/[’'`´]/g, "'").replace(/\s+/g, ' ').trim();
/** a category whose items are written into a text row as they are named (a colour, a layout) */
const TEXT_CATEGORIES = [
    { re: /\bHULL\b.*\bDECK\b.*\bCOLOU?R|\bHULL AND DECK\b/, fields: ['L', 'M'] },
    { re: /\bHULL COLOU?R/, fields: ['L'] },
    { re: /\bDECK COLOU?R/, fields: ['M'] },
    { re: /\bTUBES?\b/, fields: ['N'] },
    { re: /\bUPHOLSTER/, fields: ['K'] },
    { re: /\bFENDER/, fields: ['O'] },
    { re: /\bFLOOR/, fields: ['P'] },
    { re: /\bAFT CONFIG|\bSTERN\b/, fields: ['G'] },
    { re: /\bBOW CONFIG|\bBOW LAYOUT/, fields: ['F'] },
    { re: /\bHELM\b.*\bSEAT|\bSEATING CONFIG/, fields: ['H'] },
];
/** words that tick an option row (first the more specific, so an aft windlass isn't also a bow one) */
const TICK_RULES = [
    { re: /\bJOYSTICK\b/, field: 'AE' },
    { re: /\bAUTO ?PILOT\b/, field: 'AF' },
    { re: /\bHYDRAULIC STEERING\b/, field: 'AC' },
    { re: /\bELECTRIC(AL)? (POWER )?STEERING\b/, field: 'AD' },
    { re: /\bVESSEL ?VIEW\b/, field: 'Y' },
    { re: /\bRAYMARINE\b|\bAXIOM\b/, field: 'AI', unless: /\bAUTO ?PILOT\b/ },
    { re: /\bGARMIN\b/, field: 'AJ' },
    { re: /\bGMDSS\b/, field: 'AS' },
    { re: /\bVHF\b/, field: 'AR' },
    { re: /\bAIS\b/, field: 'AT' },
    { re: /\bFUSION\b.*\b(6|SIX) SPEAKERS?\b|\bSUBWOOFER\b|\bAMPLIFIERS?\b/, field: 'AU' },
    { re: /\bFUSION\b.*\b(4|FOUR) SPEAKERS?\b/, field: 'AV' },
    { re: /\bTHERMAL\b|\bNIGHT VISION\b/, field: 'AW' },
    { re: /\bRADAR\b/, field: 'AX' },
    { re: /\b(SEARCH|SPOT) ?LIGHT\b/, field: 'AY' },
    { re: /\b(T-?TOP|HARD ?TOP)\b.*\b(SIDE|TENTS?)\b/, field: 'BL' },
    { re: /\bT-?TOP\b/, field: 'BA', unless: /\bSIDE\b|\bTENTS?\b/ },
    { re: /\bSHOXS?\b/, field: 'BB' },
    { re: /\bTWO ROWS\b|\b2 ROWS\b|\b2ND ROW\b|\bSECOND ROW\b/, field: 'BC' },
    { re: /\bBACK-? ?TO-? ?BACK\b.*\bFRIDGES?\b/, field: 'BD' },
    { re: /\bCABIN FRIDGE\b/, field: 'BE' },
    { re: /\bWET ?BAR\b/, field: 'BF' },
    { re: /\b(2ND|SECOND) FRIDGE\b/, field: 'BG' },
    { re: /\bELECTRIC(AL)? TABLE\b/, field: 'BH' },
    { re: /\bTABLE\b/, field: 'BI', unless: /\bELECTRIC(AL)? TABLE\b/ },
    { re: /\bAFT SIDE TENTS?\b/, field: 'BM' },
    { re: /\b(SUN ?TENT|SUNSHADE|BIMINI)\b.*\bBOW\b|\bBOW\b.*\b(SUN ?TENT|SUNSHADE|BIMINI)\b/, field: 'BK' },
    { re: /\b(SUN ?TENT|SUNSHADE|BIMINI)\b.*\bAFT\b|\bAFT\b.*\b(SUN ?TENT|SUNSHADE|BIMINI)\b/, field: 'BJ' },
    { re: /\bTOWING EYES?\b/, field: 'BO' },
    { re: /\bSKI (POLE|PYLON|ARCH)\b/, field: 'BP' },
    { re: /\bFLAG ?(HOLDER|POLE|STAFF)\b/, field: 'BQ' },
    { re: /\bCABIN\b.*\bAFT\b/, field: 'BR', unless: /\bFRIDGE\b/ },
    { re: /\bCABIN\b.*\bBOW\b/, field: 'BS', unless: /\bFRIDGE\b/ },
    { re: /\bSHOWER\b/, field: 'BT' },
    { re: /\bUNDERWATER\b/, field: 'BV' },
    { re: /\bSHORE ?POWER\b/, field: 'BW' },
    { re: /\bTHRUSTER\b/, field: 'BX' },
    { re: /\bSOLAR\b/, field: 'BY' },
    { re: /\b(LED|LIGHT) BAR\b/, field: 'BZ' },
    { re: /\bPORTABLE\b.*\bFRIDGE\b|\bFRIDGE ?\/ ?FREEZER\b|\bCOOL ?BOX\b/, field: 'CA' },
    { re: /\bTOILET\b|\bWC\b/, field: 'CB' },
    { re: /\bELECTRIC(AL)? AFT HATCH\b/, field: 'CC' },
    { re: /\b(AFT|STERN) WINDLASS\b/, field: 'CL' },
    { re: /\bWINDLASS\b/, field: 'CD', unless: /\b(AFT|STERN) WINDLASS\b/ },
    { re: /\bTRIM TABS?\b|\bINTERCEPTORS?\b/, field: 'CE' },
    { re: /\bWASH ?DOWN\b/, field: 'CF' },
    { re: /\bCOFFE+ ?(MAKER|MACHINE)\b/, field: 'CG' },
    { re: /\bAIR ?CONDITION|\bA\/C\b|\bAIRCON\b/, field: 'CH' },
    { re: /\bINVERTER\b/, field: 'CI' },
    { re: /\bGENERATOR\b/, field: 'CJ' },
    { re: /\bWATER HEATER\b|\bBOILER\b|\bHOT WATER\b/, field: 'CK' },
    { re: /\b(AFT|STERN) ANCHOR\b/, field: 'CM' },
    { re: /\bANTI-? ?FOULING\b/, field: 'CO' },
    { re: /\bTRAILER\b/, field: 'CP' },
    { re: /\bPARKING COVER\b/, field: 'CQ' },
    { re: /\bCONSOLE COVER\b/, field: 'CR' },
    { re: /\bSEATS?\b.*\bCOVERS?\b|\bSUN ?DECK\b.*\bCOVERS?\b/, field: 'CS' },
    { re: /\bTOWING (SET OF )?ROPES?\b|\bSET OF ROPES\b|\bTOWING SET\b/, field: 'CT' },
    { re: /\bSHRINK ?WRAP\b/, field: 'CU' },
];
// screens: a size written beside a screen, a display or a chartplotter's name
const SCREEN_SIZES = [
    { re: /(^|\D)16 ?('{1,2}|"|”|INCH)/, field: 'AM' },
    { re: /(^|\D)12 ?('{1,2}|"|”|INCH)/, field: 'AN' },
    { re: /(^|\D)9 ?('{1,2}|"|”|INCH)/, field: 'AO' },
];
const SCREEN_WORDS = /\bSCREEN\b|\bDISPLAY\b|\bMFD\b|\bAXIOM\b|\bGPS\b|\bCHART ?PLOTTER\b|\bGARMIN\b/;
// an engine set: "2x Mercury 400R V10", "2 YANMAR 370 LV8 and monitor", "Yamaha 450 XTO", "Volvo D6-440": the make and then
// its power (a make followed by anything else, "Mercury Vessel View 7''", is a screen, not an engine)
const ENGINE = /(?:\b(\d)\s*X?\s+|\b(\d)X\s*)?\b(MERCURY|YAMAHA|VOLVO|YANMAR)\b\s+([A-Z]{0,2}\d?-?\d{2,3}[^,(]*?)(?=\s+(?:WITH|AND|OR|&|PLUS|JOYSTICK|AUTO ?PILOT)\b|[,(]|$)/;
const ENGINE_FIELD = { MERCURY: 'T', YAMAHA: 'U', VOLVO: 'V', YANMAR: 'W' };
/**
 * The order file rows an item writes, suggested from its name (and its assembly label) and its category:
 * a colour or layout category writes the item's name on its text row, an engine set writes how many and which,
 * and words in the name tick the option rows they name.
 */
export function suggestOrderCells(item) {
    const t = fold(`${item.label ?? ''} ${item.name}`);
    const cat = fold(item.category);
    const out = [];
    const add = (field, value = '') => {
        if (!out.some((c) => c.field === field))
            out.push({ field, value: orderCellIsTick(field) ? '' : value });
    };
    const words = (item.label?.trim() || item.name).replace(/\s+/g, ' ').trim().toUpperCase();
    // a colour, a layout or a seating choice: its name goes on the category's row
    const text = TEXT_CATEGORIES.find((c) => c.re.test(cat));
    if (text)
        for (const f of text.fields)
            add(f, words);
    // the power supply, chosen in its own category: the American set-up or the European one (a voltage in another
    // item's name, "shore power 230V", only describes it)
    if (/\bAC\/?AV\b|\bVOLTAGE\b|\bPOWER SUPPLY\b/.test(cat)) {
        if (/\bNON[- ]?US\b|\bEU(ROPE(AN)?)?\b|\b2[23]0 ?V\b/.test(t))
            add('AL');
        else if (/\bUSA?\b|\b110 ?V\b/.test(t))
            add('AK');
    }
    // an engine set (not a paint option for the engines)
    const eng = /\bCOLOU?R\b|\bPAINT/.test(t) ? null : ENGINE.exec(t);
    if (eng) {
        const count = eng[1] ?? eng[2];
        if (count)
            add('S', count);
        add(ENGINE_FIELD[eng[3]], eng[4].replace(/\s+/g, ' ').trim());
    }
    for (const r of TICK_RULES)
        if (r.re.test(t) && !r.unless?.test(t))
            add(r.field);
    if (SCREEN_WORDS.test(t))
        for (const s of SCREEN_SIZES)
            if (s.re.test(t))
                add(s.field);
    return out;
}
/** an item's ties: the saved ones (an empty list writes nothing), else the suggestion */
export function orderCellsFor(item) {
    return item.orderCells ? { cells: [...item.orderCells], from: 'saved' } : { cells: suggestOrderCells(item), from: 'suggested' };
}
/**
 * The boat's order file cells from its configuration: the model's standard equipment ticked (the BOM lists
 * page keeps which rows that is), the standard items kept, the options chosen, and the custom requests on the
 * additional-request rows. `cells` holds every row the configuration speaks for (any item's row, the standard
 * rows and the request rows), blank where it says no, so a revision that drops an option clears its tick;
 * a row no item is tied to is left as the Order Sheet has it.
 */
export function configuredOrderCells(items, stdFields, requests) {
    const controlled = new Set([...stdFields.filter(isOrderCellField), ...ORDER_FILE_REQUEST_KEYS]);
    for (const i of items)
        for (const c of i.cells)
            if (isOrderCellField(c.field))
                controlled.add(c.field);
    const values = {};
    for (const f of stdFields)
        if (isOrderCellField(f))
            values[f] = ORDER_FILE_TICK;
    const put = (c) => {
        if (!isOrderCellField(c.field))
            return;
        if (orderCellIsTick(c.field)) {
            values[c.field] = ORDER_FILE_TICK;
            return;
        }
        const v = c.value.trim();
        if (!v)
            return;
        const cur = values[c.field];
        // two choices on one row (a hull colour from one item, a deck colour from another) are both kept
        values[c.field] = cur && cur !== ORDER_FILE_TICK && !cur.split(' / ').includes(v) ? `${cur} / ${v}` : v;
    };
    // what a standard item taken off brings goes, unless something chosen brings it back
    for (const i of items)
        if (i.standard && !i.removed)
            i.cells.forEach(put);
    for (const i of items)
        if (i.standard && i.removed)
            for (const c of i.cells)
                delete values[c.field];
    for (const i of items)
        if (!i.standard && i.selected)
            i.cells.forEach(put);
    // the custom requests, one per row; past the last row they share it
    const reqs = requests.map((r) => r.replace(/\s+/g, ' ').trim()).filter(Boolean);
    const keys = ORDER_FILE_REQUEST_KEYS;
    reqs.forEach((r, n) => {
        const k = keys[Math.min(n, keys.length - 1)];
        values[k] = n >= keys.length ? `${values[k]} · ${r}` : r;
    });
    const out = {};
    for (const f of controlled)
        out[f] = values[f] ?? '';
    return out;
}
/** a custom request as one line of the order file: "Teak deck (x2): in the cockpit" */
export function orderRequestLine(r) {
    const qty = r.quantity && r.quantity > 1 ? ` (x${r.quantity})` : '';
    const desc = r.description?.replace(/\s+/g, ' ').trim();
    return `${r.title.trim()}${qty}${desc ? `: ${desc}` : ''}`.slice(0, 240);
}
/** the key a configured order's boat is filed under on the Order Sheet: its SAP, else its name */
export function configuredOrderKey(sap, name) {
    const digits = String(sap ?? '').replace(/\.0+$/, '').match(/\d{5,}/)?.[0];
    return digits ?? unlistedOrderKey(name);
}
//# sourceMappingURL=configuratorOrder.js.map